<template>
  <div>
  <v-container class="assessment-light tapper_container ma-auto px-4">
    <v-row align="stretch" class="mb-3">
      <v-col style="max-width: fit-content" align="center" justify="center" >
        <v-btn 
          id="tapper-reset" 
          class="me-auto py-2 reset-warning" 
          style="background-color: transparent" 
          @click="reset()" 
          outlined
          depressed
        >
          <v-icon size=30 >{{ icons.mdiSync }}</v-icon>
          <mrx-translation token="app.inputs.try_again.button"></mrx-translation>
        </v-btn>
      </v-col>
      <v-col :class="inputDisplay">
        <v-icon size=30 :color="isBPMOutofRange ? 'red' : 'primaryblue'">{{ bpmIcon }}</v-icon>
        <span class="px-2">{{ $n(bpm, 'decimal') }}</span>
        <mrx-translation class="unit-text" :renderAsHTML="true" :token="unitToken"></mrx-translation>
      </v-col>
    </v-row>

    <v-row align="center" justify="center" style="min-height: 120px">
      <v-avatar align="center" justify="center" class="pulse" size="250">
        <v-img v-show="showImageIn" :src="pulseIn" eager></v-img>
      </v-avatar>
      <v-avatar align="center" justify="center" class="pulse" size="250">
        <v-img v-show="!showImageIn" :src="pulseOut" eager></v-img>
      </v-avatar>
      <v-avatar 
        id="tapper"
        @touchstart="tap" 
        @touchend="touchEnd" 
        @mousedown="tap" 
        color=primaryblue30 
        :size="showImageIn ? '120' : '96'"
      >
        <v-img :src="tapperImage" eager></v-img>
      </v-avatar>
    </v-row>
  </v-container>
</div>
</template>

<script>
  import icons from '@/mixins/icons'
  import languageLogic from '@/mixins/languageLogic'
  import rateRanges from '@/mixins/rateRanges'
  import { mapGetters } from 'vuex'
  export default {
    mixins: [icons, languageLogic, rateRanges],
    name: 'tapper',
    model: {
      prop: 'items',
      event: 'click'
    },
    data() {
      return {
        taps: [],
        showImageIn: false,
        toggleDelay: 150,
        bpm: 0
      }
    },
    props: {
      'options': Object,
      'items': Array,
      'panelName': String,
      dataPoints: { type: Number, default: 4 },
      sensitivity: { type: Number, default: 50 },
      threshold: { type: Number, default: 10 }
    },
    computed: {
      displayResults() {
        return this.taps.length > this.threshold - 1
      },
      getTaps() {
        // Get the last this.dataPoints + 1 taps
        return this.taps.slice(this.taps.length - (this.dataPoints + 1), this.taps.length)
      },
      getDifferenceInTaps() {
        // Get the ms difference in taps
        return this.getTaps.reduce((acc, cur, index, arr) => {
          if (arr[index + 1]) {
            acc.push(arr[index + 1] - cur)
          }
          return acc
        }, [])
      },
      warningMessage() {
          return `questions.${this.panelName}.message.error`
      },
      captured() {
        // Did we find a pattern
        if (this.getTaps.length < (this.dataPoints + 1)) return false
        return this.getDifferenceInTaps.every((number, i) => {
          return i + 1 === this.getDifferenceInTaps.length || i === 0 || Math.abs((this.getDifferenceInTaps[i + 1] - number)) < this.sensitivity
        })
      },
      calculateBpmAsAverage() {
        return Math.round((this.threshold - 1) / ((this.taps[this.threshold - 1] - this.taps[0]) / 60000))
      },
      calculateBpmAsDifference() {
        return Math.round(this.dataPoints / ((this.getTaps[this.dataPoints] - this.getTaps[0]) / 60000))
      },
      inputDisplay() {
        const colorClass = this.isBPMOutofRange ? 'red--text' : 'primaryblue--text'
        return `${colorClass} bpm_text text-end`
      },
      recordedMessage() {
        return `questions.${this.panelName}.recorded_message`
      },
      unitToken() {
        return `units.${this.options.unit}.short`
      },
      tapperImage() {
        return `${this.sharedPath}${this.panelName}.png`
      },
      pulseIn() {
        return `${this.sharedPath}pulse_in.svg`
      },
      pulseOut() {
        return `${this.sharedPath}pulse_out.svg`
      },
      bpmIcon() {
        if (this.panelName === 'respiratory_rate') {
          return this.icons.mdiLungs
        } else {
          return this.icons.mdiHeart
        }
      },
      isBPMOutofRange() {
        return this.BPMoutOfRange(this.bpm, false)
      },
      ...mapGetters({ sharedPath: 'v4/sharedPath', settings: 'v4/settings/get' })
    },
    methods: {
      bpmMilliseconds(bpm) {
        return (60 / bpm) * 1000
      },
      reset() {
        this.bpm = 0
        this.taps = []
        this.showImageIn = false
        this.items[0].selected = false
        this.items[0].value = 0
        this.clearRangeValidation()
        this.$track(this.$analyticsEvents.TAPPER_RESET, {
          component: 'Tapper',
          panel_name: this.panelName
        })
      },
      touchEnd(e) {
        e.preventDefault() // prevents event from triggering mousedown and therefore firing twice on mobile
      },
      tap(e) {
        e.preventDefault() // prevents event from triggering mousedown and therefore firing twice on mobile
        
        if (this.bpm > 0) return

        if (this.taps.length === 0) {
          this.$track(this.$analyticsEvents.TAPPER_STARTED, {
            component: 'Tapper',
            panel_name: this.panelName
          })
        }

        if (this.bpm === 0) {
          this.taps.push(Date.now())
          this.toggleImageTap()
        }

        if (this.captured) {
          this.bpm = this.calculateBpmAsDifference
        }
        else if (this.displayResults) {
          this.bpm = this.calculateBpmAsAverage
        }

        if (this.bpm > 0) {
          this.automateBpm()
          const validation = this.validateObservationRange(this.bpm)
          const accepted = validation.severity !== 'blocking'
          this.items[0].selected = accepted
          this.items[0].value = accepted ? this.bpm : null
          if (accepted) this.$emit('selectEvent')
          this.$track(this.$analyticsEvents.TAPPER_COMPLETED, {
            component: 'Tapper',
            panel_name: this.panelName,
            bpm_value: this.bpm,
            tap_count: this.taps.length,
            is_out_of_range: this.isBPMOutofRange,
            measurement_method: this.captured ? 'pattern_detected' : 'average',
            measurement_duration_ms: this.taps[this.taps.length - 1] - this.taps[0]
          })
        }
      },
      toggleImageTap() {
        this.showImageIn = true
        setTimeout(() => {
          this.showImageIn = false
        }, this.toggleDelay)
      },
      automateBpm() {
        if (this.bpm > 0) {
          this.toggleImageTap()
          setTimeout(() => { this.automateBpm() }, this.bpmMilliseconds(this.bpm))
        }
      },
      showEnteredRate() {
        if (this.items[0].selected) {
          this.bpm = this.items[0].value
          this.validateObservationRange(this.bpm)
          this.automateBpm()
        }
        else {
          this.reset()
        }
      }
    },
    mounted() {
      this.showEnteredRate()
    },
    watch: {
      panelName: function() {
        this.showEnteredRate()
      }
    }
  }
</script>

<style scoped>
  img {
    width: 100%;
    display: block;
    padding: 0;
    margin: 0 0;
    max-height: 100%;
    max-width: 100%;
  }

  .tapper {
    background-color: green;
    width: 100px;
    height: 100px;
  }

  .tapper_container {
    border-radius: 16px;
    min-height: 290px;
    z-index: 0;
    position: relative;
  }

  .pulse {
    z-index: -1;
    text-align: center;
    position: absolute;
  }

  .bpm_text {
    font-size: 20px;
    font-weight: 600;
  }

  .unit-text {
    display: inline;
  }

  .error_message {
    background-color: var(--danger);
    color: white;
    font-weight: 400;
    font-size: 12px;
    border-radius: 5px;
    padding: 6px 14px 6px 14px;
  }
</style>
